/**
 * CoverageIndicator — source coverage / permission state rendered strictly
 * from the ODE-P10-T06 coverage payload (ODE-P10-T07, REQ-099/109).
 *
 * Every value comes from the T06 `coverage` block (source status, last seen,
 * permission state, observed count, overall). A missing capability is a
 * VISIBLE gap — NONE/UNKNOWN overall, non-ACTIVE status, or a
 * REVOKED/POLICY_GATED/UNKNOWN permission state render an explicit gap note.
 * The component never fabricates or infers coverage.
 *
 * Traces to: ODE-PRD-REQ-098, ODE-PRD-REQ-099, ODE-PRD-REQ-109,
 *            ODE-PRD-REQ-110.
 */

import type { CoverageSummary, SourceCoverageRecord } from './types.js';

const SOURCE_LABELS: Record<SourceCoverageRecord['sourceKind'], string> = {
  CONTACT_SNAPSHOT: 'Kontak (snapshot)',
  CALL_METADATA: 'Panggilan (metadata)',
  WHATSAPP_NOTIFICATION: 'Notifikasi WhatsApp (teramati)',
};

const PERMISSION_LABELS: Record<SourceCoverageRecord['permissionState'], string> = {
  GRANTED: 'Diberikan',
  REVOKED: 'Dicabut',
  NOT_REQUIRED: 'Tidak diperlukan',
  POLICY_GATED: 'Dibatasi kebijakan',
  UNKNOWN: 'Tidak diketahui',
};

const OVERALL_LABELS: Record<CoverageSummary['overall'], string> = {
  FULL: 'Cakupan penuh',
  PARTIAL: 'Cakupan sebagian',
  NONE: 'Tidak ada cakupan',
  UNKNOWN: 'Cakupan tidak diketahui',
};

function isGap(source: SourceCoverageRecord): boolean {
  return (
    source.status !== 'ACTIVE' ||
    source.permissionState === 'REVOKED' ||
    source.permissionState === 'POLICY_GATED' ||
    source.permissionState === 'UNKNOWN'
  );
}

export function CoverageIndicator({ coverage }: { readonly coverage: CoverageSummary }): JSX.Element {
  const gaps = coverage.sources.filter(isGap);
  return (
    <section className="coverage" data-testid="coverage-indicator" aria-label="Indikator cakupan sumber">
      <h4>
        Cakupan sumber: <span data-testid="coverage-overall">{OVERALL_LABELS[coverage.overall]}</span>
      </h4>
      <ul className="coverage-list">
        {coverage.sources.map((source) => (
          <li
            key={source.sourceKind}
            data-testid={`coverage-source-${source.sourceKind}`}
            className={isGap(source) ? 'coverage-gap' : 'coverage-ok'}
          >
            <span className="coverage-source">{SOURCE_LABELS[source.sourceKind]}</span>
            <span data-testid={`coverage-status-${source.sourceKind}`}>{source.status}</span>
            <span>izin: {PERMISSION_LABELS[source.permissionState]}</span>
            <span>terakhir teramati: {source.lastSeenAt ?? 'tidak pernah'}</span>
            <span>observasi: {source.observedCount}</span>
          </li>
        ))}
      </ul>
      {gaps.length > 0 && (
        <p role="alert" data-testid="coverage-gap-note" className="coverage-gap-note">
          Kesenjangan cakupan terlihat: {gaps.length} sumber tidak aktif / izin tidak tersedia — data
          yang hilang TIDAK diisi atau diklaim (REQ-109).
        </p>
      )}
      {coverage.sources.length === 0 && (
        <p role="alert" data-testid="coverage-gap-note" className="coverage-gap-note">
          Tidak ada catatan cakupan dari backend — cakupan tidak diketahui, tidak diklaim penuh.
        </p>
      )}
    </section>
  );
}